/* Shared validation and image/text rasterization for engraving originals. */
(() => {
  'use strict';
  const supported = 'PDF, JPG/JPEG, PNG, WebP, SVG, GIF or TXT';
  const cache = new WeakMap();
  const unsafeStyle = text => /@import|javascript:/i.test(text) ||
    [...text.matchAll(/url\s*\(([^)]*)\)/gi)].some(m => !m[1].trim().replace(/^['"]|['"]$/g, '').startsWith('#'));
  async function inspect(file) {
    if (!file || !file.size || file.size > 10 * 1024 * 1024) throw Error('Choose a non-empty artwork file up to 10 MB.');
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png', 'webp', 'svg', 'gif', 'txt'].includes(ext)) throw Error(`Use ${supported}. Export Word, Illustrator, EPS or other files as PDF or PNG first.`);
    const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    const ascii = new TextDecoder().decode(bytes);
    const starts = (...sig) => sig.every((v, i) => bytes[i] === v);
    if (ext === 'pdf') {
      if (!ascii.startsWith('%PDF-')) throw Error('This is not a valid PDF. Re-export your artwork and try again.');
      return {kind: 'pdf', ext};
    }
    if (ext === 'txt') {
      if (file.size > 100000) throw Error('Text files must be under 100 KB.');
      let text;
      try { text = new TextDecoder('utf-8', {fatal: true}).decode(await file.arrayBuffer()).trim(); }
      catch { throw Error('Save your text file as UTF-8 plain text and try again.'); }
      if (!text || /[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(text)) throw Error('Upload a plain-text file containing visible text.');
      if (text.length > 2000) throw Error('Use up to 2,000 characters in a text artwork file.');
      return {kind: 'text', ext, text};
    }
    const mime = {jpg:'image/jpeg', jpeg:'image/jpeg', png:'image/png', webp:'image/webp', gif:'image/gif', svg:'image/svg+xml'}[ext];
    if (ext === 'svg') {
      const text = await file.text();
      const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
      if (doc.querySelector('parsererror') || doc.documentElement.localName !== 'svg' || /<!DOCTYPE|<!ENTITY/i.test(text)) throw Error('Upload a valid, self-contained SVG.');
      for (const el of doc.querySelectorAll('*')) {
        if (['script','foreignobject','iframe','object','embed','image','animate','animatetransform','animatemotion','set'].includes(el.localName.toLowerCase())) throw Error('SVG must contain static vector artwork only. Export this file as PDF or PNG.');
        for (const attr of el.attributes) {
          if (/^on/i.test(attr.name) || (/href$/i.test(attr.name) && !attr.value.startsWith('#')) ||
              unsafeStyle(attr.value)) throw Error('SVG external content is not supported. Export as PDF or PNG.');
        }
        if (el.localName === 'style' && unsafeStyle(el.textContent)) throw Error('SVG external styles are not supported. Export as PDF or PNG.');
      }
    } else {
      const valid = (['jpg','jpeg'].includes(ext) && starts(255,216,255)) ||
        (ext === 'png' && starts(137,80,78,71,13,10,26,10)) ||
        (ext === 'gif' && /^GIF8[79]a/.test(ascii)) ||
        (ext === 'webp' && ascii.startsWith('RIFF') && ascii.slice(8,12) === 'WEBP');
      if (!valid) throw Error('The file contents do not match its image format. Re-export the image and try again.');
    }
    return {kind:'image', ext, mime};
  }
  function validate(file) {
    if (!file) return Promise.reject(Error('Upload your logo or text artwork.'));
    if (!cache.has(file)) cache.set(file, inspect(file));
    return cache.get(file);
  }
  async function rasterize(file, info) {
    const canvas = document.createElement('canvas');
    if (info.kind === 'text') {
      const lines = info.text.replace(/\r\n?/g, '\n').split('\n').flatMap(line => line.match(/.{1,70}/gu) || ['']);
      if (lines.length > 30) throw Error('Use at most 30 lines for a readable text engraving.');
      canvas.width = 2400; canvas.height = Math.max(240, lines.length * 100 + 80);
      const ctx = canvas.getContext('2d');
      ctx.font = 'bold 64px Arial, sans-serif'; ctx.fillStyle = '#000'; ctx.textBaseline = 'top';
      lines.forEach((line,i) => ctx.fillText(line, 40, 40 + i * 100, 2320));
      return canvas;
    }
    const url = URL.createObjectURL(new Blob([file], {type:info.mime}));
    try {
      const image = new Image();
      await new Promise((resolve,reject) => { image.onload=resolve; image.onerror=()=>reject(Error('This image could not be opened. Export as PNG or JPEG and try again.')); image.src=url; });
      if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth * image.naturalHeight > 40000000) throw Error('Use an image with valid dimensions, no larger than 40 megapixels.');
      const scale = Math.min(1, 2400 / Math.max(image.naturalWidth,image.naturalHeight));
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      canvas.getContext('2d').drawImage(image,0,0,canvas.width,canvas.height);
      return canvas;
    } finally { URL.revokeObjectURL(url); }
  }
  window.stEngravingFiles = {validate, rasterize, supported};
})();